// ─────────────────────────────────────────────────────────────────────────
// Promise-based confirm()/alert() replacements.
//
// window.confirm()/alert() are unstyled (raw browser chrome, shows the
// page's URL, can't match the app's light/dark theme) and block the whole
// tab synchronously. These render the same .modal-overlay/.modal markup
// used everywhere else in the app (see style.css and e.g. views/home.js's
// create-course modal), so a "Delete forever?" prompt looks like it's part
// of Learning Lab instead of a jarring browser popup.
//
// Usage mirrors the native functions but is async:
//   if (!(await confirmDialog('Delete this?'))) return;
//   await alertDialog('Something went wrong.');
// ─────────────────────────────────────────────────────────────────────────

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function buildOverlay(title, message, buttonsHtml) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" role="alertdialog" aria-modal="true" aria-labelledby="dlg-title">
      <h2 id="dlg-title">${escapeHtml(title)}</h2>
      <p style="color:var(--text-dim);white-space:pre-wrap;margin:0;">${escapeHtml(message)}</p>
      <div class="modal-actions">${buttonsHtml}</div>
    </div>
  `;
  return overlay;
}

/**
 * Replaces window.confirm(). Resolves true (OK) or false (Cancel, Escape,
 * or clicking the backdrop).
 */
export function confirmDialog(message, { title = 'Are you sure?', okText = 'OK', cancelText = 'Cancel', danger = false } = {}) {
  return new Promise((resolve) => {
    const overlay = buildOverlay(title, message, `
      <button class="btn" id="dlg-cancel-btn">${escapeHtml(cancelText)}</button>
      <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" id="dlg-ok-btn">${escapeHtml(okText)}</button>
    `);
    document.body.appendChild(overlay);

    function close(result) {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    }
    function onKey(e) {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(false); });
    overlay.querySelector('#dlg-cancel-btn').addEventListener('click', () => close(false));
    overlay.querySelector('#dlg-ok-btn').addEventListener('click', () => close(true));
    overlay.querySelector('#dlg-ok-btn').focus();
  });
}

/**
 * Replaces window.alert(). Resolves once the user dismisses it.
 */
export function alertDialog(message, { title = 'Notice', okText = 'OK' } = {}) {
  return new Promise((resolve) => {
    const overlay = buildOverlay(title, message, `
      <button class="btn btn-primary" id="dlg-ok-btn">${escapeHtml(okText)}</button>
    `);
    document.body.appendChild(overlay);

    function close() {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve();
    }
    function onKey(e) { if (e.key === 'Escape' || e.key === 'Enter') close(); }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('#dlg-ok-btn').addEventListener('click', close);
    overlay.querySelector('#dlg-ok-btn').focus();
  });
}

/**
 * Replaces window.prompt(). Resolves with the trimmed entered text, or
 * `null` on Cancel/Escape/backdrop click — same contract as native
 * prompt() (an empty submitted value also resolves `null`, matching how
 * callers already treat window.prompt()'s '' vs null).
 */
export function promptDialog(message, { title = 'Enter a value', defaultValue = '', okText = 'OK', cancelText = 'Cancel' } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <form class="modal" role="dialog" aria-modal="true" aria-labelledby="dlg-title">
        <h2 id="dlg-title">${escapeHtml(title)}</h2>
        ${message ? `<p style="color:var(--text-dim);white-space:pre-wrap;margin:0 0 12px;">${escapeHtml(message)}</p>` : ''}
        <label class="field"><input type="text" id="dlg-input" value="${escapeHtml(defaultValue)}"></label>
        <div class="modal-actions">
          <button type="button" class="btn" id="dlg-cancel-btn">${escapeHtml(cancelText)}</button>
          <button type="submit" class="btn btn-primary" id="dlg-ok-btn">${escapeHtml(okText)}</button>
        </div>
      </form>
    `;
    document.body.appendChild(overlay);
    const form = overlay.querySelector('form');
    const input = overlay.querySelector('#dlg-input');

    function close(result) {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    }
    function onKey(e) { if (e.key === 'Escape') close(null); }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(null); });
    overlay.querySelector('#dlg-cancel-btn').addEventListener('click', () => close(null));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const value = input.value.trim();
      close(value || null);
    });
    input.focus();
    input.select();
  });
}
